import type { ReactNode, TableHTMLAttributes } from 'react'
import { EmptyState } from '@/components/EmptyState'
import { LoadingState } from '@/components/LoadingState'

interface DataTableProps extends TableHTMLAttributes<HTMLTableElement> {
  dense?: boolean
  clickable?: boolean
  loading?: boolean
  empty?: boolean
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: ReactNode
  loadingColumns?: number
  loadingRows?: number
  /** Fill parent flex pane and scroll inside (default true for hubs). */
  fill?: boolean
  children: ReactNode
}

/**
 * Hub table wrapper — MBA-style bordered surface with sticky header + pane scroll.
 * Parent must be a flex `min-h-0` pane (use inside HubPage body).
 */
export function DataTable({
  dense = true,
  clickable = false,
  loading = false,
  empty = false,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  emptyAction,
  loadingColumns = 5,
  loadingRows = 8,
  fill = true,
  className = '',
  children,
  ...tableProps
}: DataTableProps) {
  if (loading) {
    return (
      <LoadingState
        variant="skeleton-table"
        columns={loadingColumns}
        rows={loadingRows}
        label="Loading…"
      />
    )
  }

  if (empty) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
  }

  const tableClass = [
    'table',
    dense ? 'table--dense' : '',
    clickable ? 'table--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`table-pane${fill ? ' table-pane--fill' : ''}`}>
      <div className="table-pane__scroll">
        <table className={tableClass} {...tableProps}>
          {children}
        </table>
      </div>
    </div>
  )
}

export function TableHeader({
  sticky = true,
  children,
  className = '',
}: {
  sticky?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <thead className={`${sticky ? 'table__head--sticky' : ''} ${className}`.trim()}>
      {children}
    </thead>
  )
}

export function TableBody({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return <tbody className={className}>{children}</tbody>
}
